import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  useMediaQuery,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BarChart3, ShoppingCart, ArrowRight, Package } from 'lucide-react';

function Dashboard() {
  const [todaysSales, setTodaysSales] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token found! Redirecting to login...");
      navigate("/login");
      return;
    }
    console.log("Using Token:", token);

    axios.get("https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/auth/profile", {
      headers: { "Authorization": `Bearer ${token}` },
      withCredentials: true,
    })
      .then(res => console.log("User authenticated:", res.data))
      .catch(error => {
        console.error("Authentication failed:", error.response?.data);
        navigate("/login");
      });

    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);
        const response = await axios.get('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/todays-sales');
        const { salesData, ordersToday } = response.data;
        setTodaysSales(salesData);
        setOrders(ordersToday);
        console.log('sales', salesData);
        console.log('orders', ordersToday);
      } catch (error) {
        console.error("Error fetching today's sales and orders:", error);
        setError('Failed to fetch data. Please check your connection or try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleViewGraphs = () => navigate('/todays-graphs');
  const titleClick = (productId) => navigate(`/todays-graphs/product/${productId}`);

  const isMobile = useMediaQuery('(max-width:768px)');
  const totalQuantity = todaysSales.reduce((sum, p) => sum + p.quantity, 0);
  const totalRevenue = orders.reduce((sum, o) => sum + parseFloat(o.total_price || 0), 0);

  return (
    <Box className="fade-in">
      <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 700, letterSpacing: '-0.02em' }}>
        Dashboard
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Today's sales overview
      </Typography>

      {loading ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Box key={i} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Skeleton variant="text" width="50%" sx={{ mb: 1, fontSize: '0.75rem' }} />
                <Skeleton variant="text" width="40%" sx={{ fontSize: '2rem' }} />
              </Box>
            ))}
          </Box>
          <Skeleton variant="text" width="20%" sx={{ fontSize: '1.1rem', mb: 2 }} />
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 4 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Box key={i} sx={{
                display: 'grid', gridTemplateColumns: '1fr 120px',
                px: 2, py: 1.5,
                borderBottom: i === 4 ? 'none' : '1px solid', borderColor: 'divider',
              }}>
                <Skeleton variant="text" width="65%" />
                <Skeleton variant="text" width="50%" sx={{ ml: 'auto' }} />
              </Box>
            ))}
          </Box>
        </>
      ) : error ? (
        <Box sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'error.main', bgcolor: 'rgba(235,87,87,0.06)' }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      ) : (
        <>
          {/* Stats Row */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
            <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#2383e2' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
                <Package size={14} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.7rem' }}>
                  Products Sold
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                {todaysSales.length}
              </Typography>
            </Box>

            <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#10b981' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
                <ShoppingCart size={14} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.7rem' }}>
                  Units Sold
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                {totalQuantity}
              </Typography>
            </Box>

            <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#f59e0b' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
                <BarChart3 size={14} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.7rem' }}>
                  Revenue
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                ${totalRevenue.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* Sales Table */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Today's Sales
            </Typography>
            {todaysSales.length > 0 ? (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 120px',
                  px: 2, py: 1.25,
                  bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(55,53,47,0.024)',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary', fontSize: '0.7rem' }}>
                    Product
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary', textAlign: 'right', fontSize: '0.7rem' }}>
                    Qty Sold
                  </Typography>
                </Box>
                {todaysSales.map((product) => (
                  <Box
                    key={product.shopifyId}
                    onClick={() => titleClick(product.shopifyId)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 120px',
                      px: 2, py: 1.5,
                      cursor: 'pointer',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      transition: 'background 0.1s',
                      '&:hover': { bgcolor: 'action.hover' },
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                      {product.title}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 600 }}>
                      {product.quantity}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No sales data available for today.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Orders Table */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Orders Details
            </Typography>
            {orders.length > 0 ? (
              isMobile ? (
                /* Mobile: Card-based layout */
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {orders.map((order) => (
                    <Box key={order.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                          #{order.id}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          ${order.total_price}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                        {order.customer ? `${order.customer.first_name || 'Unknown'} ${order.customer.last_name || ''}`.trim() : 'Unknown'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                        {new Date(order.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                      </Typography>
                      {order.line_items?.map((item) => (
                        <Typography key={item.product_id} variant="caption" sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}>
                          {item.title} (x{item.quantity})
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </Box>
              ) : (
                /* Desktop: Table layout */
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', overflowX: 'auto' }}>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(80px,1fr) minmax(100px,1.5fr) minmax(140px,2fr) minmax(120px,1.5fr) 90px minmax(140px,2fr)',
                    px: 2, py: 1.25, minWidth: 700,
                    bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(55,53,47,0.024)',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}>
                    {['Order ID', 'Customer', 'Email', 'Date', 'Total', 'Products'].map((h) => (
                      <Typography key={h} variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary', fontSize: '0.7rem' }}>
                        {h}
                      </Typography>
                    ))}
                  </Box>
                  {orders.map((order) => (
                    <Box key={order.id} sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(80px,1fr) minmax(100px,1.5fr) minmax(140px,2fr) minmax(120px,1.5fr) 90px minmax(140px,2fr)',
                      px: 2, py: 1.5, minWidth: 700,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                      '&:last-child': { borderBottom: 'none' },
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{order.id}</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {order.customer ? `${order.customer.first_name || 'Unknown'} ${order.customer.last_name || ''}`.trim() : 'Unknown'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        {order.customer?.email || 'No Email'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {new Date(order.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        ${order.total_price}
                      </Typography>
                      <Box>
                        {order.line_items?.map((item) => (
                          <Typography key={item.product_id} variant="caption" sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}>
                            {item.title} (x{item.quantity})
                          </Typography>
                        )) || <Typography variant="caption" color="text.secondary">No Products</Typography>}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )
            ) : (
              <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No orders available for today.
                </Typography>
              </Box>
            )}
          </Box>

          {/* View Graphs button */}
          <Button
            variant="contained"
            onClick={handleViewGraphs}
            endIcon={<ArrowRight size={16} />}
            fullWidth={isMobile}
            sx={{
              bgcolor: '#2383e2',
              '&:hover': { bgcolor: '#1b6ec2' },
              px: 3,
              py: 1,
            }}
          >
            View Graphs of Today's Sales
          </Button>
        </>
      )}
    </Box>
  );
}

export default Dashboard;
